/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
// import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProduct,
    HasProductContext,
    withProduct,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { Typography } from "@insite/mobius/index";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CompareProductsCheckbox from "@monsma/App/Components/CompareProductsCheckbox/CompareProductsCheckbox";
import MessagePanel from "@monsma/App/Components/MessagePanel/MessagePanel";
import { hasEnoughInventory } from "@monsma/App/Handlers/Data/Products/ProductsSelectors";
import useOverlayBadge from "@monsma/App/Hooks/useOverlayBadge";
import ProductAttributes, { ProductAttributesStyles } from "@monsma/Overrides/Components/ProductAttributes";
import { screen } from "@monsma/XDL/Helpers";
import { ellipsis, mergeWithCss } from "@monsma/XDL/Utilities";
import { imageHoverEffect, labelStyle, sharedProductAddToListLinkStyles } from "@monsma/XDL/Utilities/sharedStyles";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    userName: state.context.session.userName,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    productSettings: getSettingsCollection(state).productSettings,
    canAddToCart: canAddToCart(state, props.productContext.product, props.productContext.productInfo, false, false),
    hasEnoughInventory: hasEnoughInventory(state, props.productContext),
});

const mapDispatchToProps = {
    sendProductRecommendationTracking,
};
interface OwnProps {
    id?: string;
    view?: string;
    showAddToCart: boolean;
    otherMiddleActions?: () => React.ReactNode;
    otherActions?: () => React.ReactNode;
    productTileShortView?: boolean;
    extendedStyles?: ProductTileStyles;
    priceShowSavings?: boolean;
    priceShowSavingsAmount?: boolean;
    priceShowSavingsPercent?: boolean;
    productUnitOfMeasureDisabling?: boolean;
    productNotes?: string;
    productQuantityDisabling?: boolean;
    updateQtyActions?: () => React.ReactNode;
    titleVariant?: string;
    recommendationId?: number;
}

type Props = OwnProps &
    ProductCardSelections &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasProduct &
    HasProductContext;

export interface ProductTileStyles {
    productTileWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    notAuthorizedMessage?: TypographyPresentationProps;
    productDescriptionWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productPrice?: ProductPriceStyles;
    productAvailabilityWrapper?: InjectableCss;
    attributeTypesWrapper?: InjectableCss;
    qtyAddToCartWrapper?: InjectableCss;
    productTileActions?: InjectableCss;
    productBrandLabelText?: TypographyPresentationProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    attributes?: ProductAttributesStyles;
    outOfStockInfoMessageWrapper?: InjectableCss;
    middleSection?: InjectableCss;
    bottomSection?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productTileStyles: ProductTileStyles = {
    productTileWrapper: {
        css: css`
            background: ${getColor("common.background")};
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 6px;
            padding: 20px 20px 25px;
            line-height: 0;
            display: flex;
            flex-direction: column;

            &.list-view {
                width: 100%;
                flex-flow: row wrap;
                padding: 20px;
                border-radius: 0;
                border-bottom: 0;

                ${screen("lt", "sm")} {
                    flex-direction: column;
                }
            }

            &.short-view {
                background: transparent;
                border: 0;
                border-radius: 0;
                padding: 0;
                width: 100%;
            }
        `,
    },
    productImageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 220px;
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 4px;
            margin-bottom: 20px;
            background: ${getColor("common.background")};
            line-height: 1.5;
            overflow: hidden;

            &&.badge {
                margin-bottom: 0;
            }

            &:not(.badge) {
                margin-right: 20px;
            }

            &.list-view {
                width: 105px;
                height: 105px;
                flex: 0 0 auto;
                margin-bottom: 0;

                ${screen("lt", "sm")} {
                    width: 100%;
                    height: 220px;
                    margin-bottom: 20px;
                }
            }

            &.short-view {
                height: 168px;
                margin-bottom: 15px;
                max-width: 99.3%;
                margin-right: 0;
            }

            * {
                height: 100%;
            }
        `,
    },
    productDescriptionWrapper: {
        css: css`
            height: 38px;
            overflow: hidden;
            margin-bottom: 30px;
            margin-top: 5px;

            ${ellipsis(2, 19)};

            a > span {
                line-height: 19px;
                font-size: 14px;
                font-weight: 700;
            }

            &.list-view {
                margin-bottom: 14px;
            }

            &.short-view {
                margin-bottom: 17px;
            }
        `,
    },
    notAuthorizedMessage: {
        css: css`
            color: ${getColor("text.main")};
            line-height: 22px;
            font-size: 13px;
            word-break: break-word;

            &.add-margin {
                margin-bottom: 15px;
            }

            &.short-view {
                display: none;
            }
        `,
    },
    productBrandWrapper: {
        css: css`
            line-height: 22px;
            display: inline-flex;
            align-items: center;

            &.short-view {
                display: none;
            }
        `,
    },
    productBrandStyles: {
        nameLink: {
            css: css`
                width: 100%;
                ${ellipsis(1, 22)};
            `,
        },
    },
    productBrandLabelText: {
        css: css`
            ${labelStyle};
        `,
    },
    productDescriptionStyles: {},
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 15px;

            &.list-view {
                margin-bottom: 20px;
            }

            &.short-view {
                margin-bottom: 0;
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                margin-right: 20px;
                flex: 0;
            `,
        },
    },
    productAddToCartButtonStyles: {
        sizeVariant: "small",
        css: css`
            width: 100%;
            flex: 1;
        `,
    },
    productAddToListLinkStyles: sharedProductAddToListLinkStyles,
    productPrice: {
        price: {
            priceText: {
                css: css`
                    font-size: 16px;
                    line-height: 20px;
                    color: ${getColor("common.ui10")};
                    font-weight: 700;
                `,
            },
            errorText: {
                css: css`
                    line-height: 20px;
                    color: ${getColor("common.ui10")};
                `,
            },
            unitOfMeasureText: {
                css: css`
                    color: ${getColor("text.main")};
                    font-size: 13px;
                    line-height: 22px;
                    display: inline-flex;
                `,
            },
        },
        quoteMessage: {
            text: {
                lineHeight: "20px",
                css: css`
                    margin-right: 5px;
                `,
            },
        },
    },
    productAvailabilityWrapper: {
        css: css`
            margin-bottom: 15px;
            line-height: 20px;
            display: none;
        `,
    },
    attributeTypesWrapper: {
        css: css`
            margin-bottom: 15px;
            line-height: 20px;

            &.short-view {
                display: none;
            }
        `,
    },
    productAvailabilityStyles: {
        errorText: {
            css: css`
                line-height: 20px;
                color: ${getColor("common.ui10")};
            `,
        },
        inventoryMessage: {},
    },
    qtyAddToCartWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 25px;

            .list-view {
                margin-bottom: 30px;
            }

            &.short-view {
                display: none;
            }
        `,
    },
    productTileActions: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;

            &.more-than-one-action {
                justify-content: space-between;
            }

            &.short-view {
                display: none;
            }
        `,
    },
    attributes: {},
    outOfStockInfoMessageWrapper: {
        css: css`
            margin-bottom: 20px;
            margin-top: -8px;

            .list-view {
                margin-top: -5px;
            }

            &.short-view {
                display: none;
            }
        `,
    },
    middleSection: {
        css: css`
            &.list-view {
                flex: 1;

                ${screen("gt", "md")} {
                    padding-right: 20px;
                }

                .productAvailabilityWrapper {
                    display: block;
                    margin-bottom: 0;
                }

                ${screen("lte", "sm")} {
                    flex: 10 1 260px;
                    margin-bottom: 20px;
                    margin-right: 5px;
                }

                ${screen("lte", "xs")} {
                    flex: 1;
                    margin: 0;
                }
            }
        `,
    },
    bottomSection: {
        css: css`
            &:not(.list-view) {
                flex: 1;
                display: flex;
                flex-direction: column;

                &:not(.short-view) {
                    .productAvailabilityWrapper {
                        display: block;
                    }
                }
            }

            &.list-view {
                width: 100%;
                margin-top: 20px;

                ${screen("gt", "md")} {
                    width: 220px;
                    margin-top: 0;
                }

                ${screen("lte", "sm")} {
                    flex: 1 0 200px;
                    margin-top: 0;
                }

                ${screen("lte", "xs")} {
                    flex: 1;
                    margin-top: 20px;
                }
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                &:hover,
                &:focus {
                    > div {
                        &:after {
                            opacity: 1;
                        }
                    }
                }
            `,
        },
        image: {
            css: css`
                width: 100%;

                img {
                    width: 100%;
                    object-fit: contain;
                    object-position: center;
                }

                ${imageHoverEffect};
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
            margin-bottom: 20px;
            height: max-content;
            &:has(.list-view) {
                margin-right: 20px;

                ${screen("lt", "sm")} {
                    margin-right: 0;
                }
            }
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            > div {
                line-height: 19px;
            }
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductTile: React.FC<Props> = ({
    product,
    productContext,
    id,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    showAvailability,
    showAttributes,
    showCompare,
    otherMiddleActions,
    otherActions,
    productTileShortView,
    extendedStyles,
    view,
    userName,
    currentUserIsGuest,
    productSettings,
    canAddToCart,
    hasEnoughInventory,
    priceShowSavings,
    priceShowSavingsAmount,
    priceShowSavingsPercent,
    productUnitOfMeasureDisabling,
    productNotes,
    productQuantityDisabling,
    updateQtyActions,
    titleVariant,
    recommendationId,
    sendProductRecommendationTracking,
}: Props) => {
    const [styles] = React.useState(() => mergeWithCss(productTileStyles, extendedStyles));
    const authorized = userName && !currentUserIsGuest;
    const { attributeTypes } = product;
    const ableToAddToCart = productSettings.canAddToCart && hasEnoughInventory && canAddToCart;
    const hasAttributeTypes = attributeTypes && attributeTypes.length > 0;
    const addToListAction = authorized && showAddToList;
    const isThereAnyAction = addToListAction || showCompare || otherActions;

    const isThereMoreThanOneAction = () => {
        if (addToListAction && showCompare) {
            return true;
        }

        if (addToListAction && otherActions) {
            return true;
        }

        return !!(showCompare && otherActions);
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    // xng-change: change import
    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <StyledWrapper
            {...styles.productTileWrapper}
            className={`productTileWrapper ${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
        >
            {showImage && !showOverlayBadge && (
                <StyledWrapper
                    {...styles.productImageWrapper}
                    className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
                >
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                </StyledWrapper>
            )}
            {showImage && showOverlayBadge && overlayBadge && (
                <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge">
                    <StyledWrapper
                        {...styles.productImageWrapper}
                        className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"} badge`}
                    >
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...overlayBadgeWrapperStyles}>
                        <Badge
                            type={overlayBadge.badgeType}
                            value={
                                overlayBadge.badgeType === "Image"
                                    ? overlayBadge.largeImageBadgePath
                                    : overlayBadge.displayText
                            }
                            badgeTextColor={overlayBadge.textColorHexCode}
                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                            textBadgeCss={styles.badgeText}
                            imageBadgeProps={styles.badgeImageProps}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.middleSection} className={`${view === "List" && "list-view"}`}>
                {showPartNumbers && (
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showCustomerName={false}
                        showManufacturerItem={false}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                )}
                {showBrand && product.brand && (
                    <StyledWrapper
                        {...styles.productBrandWrapper}
                        className={`${productTileShortView && "short-view"}`}
                    >
                        <Typography {...styles.productBrandLabelText}>{translate("Brand")}</Typography>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                {showTitle && (
                    <StyledWrapper
                        {...styles.productDescriptionWrapper}
                        className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
                    >
                        {titleVariant && (
                            <Typography
                                variant={titleVariant as any}
                                css={css`
                                    margin-bottom: 0;
                                    font-weight: unset;
                                    line-height: 0;
                                `}
                            >
                                <ProductDescription
                                    product={productContext}
                                    onClick={() => trackProductRecommendation(true)}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            </Typography>
                        )}
                        {!titleVariant && (
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        )}
                    </StyledWrapper>
                )}
                {showAvailability && authorized && (
                    <StyledWrapper {...styles.productAvailabilityWrapper} className="productAvailabilityWrapper">
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    </StyledWrapper>
                )}
                {otherMiddleActions && otherMiddleActions()}
            </StyledWrapper>
            {showTextBadges && textBadges && (
                <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
            )}
            {showImageBadges && imageBadges && (
                <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
            )}

            {/* XNG-Changes: Add hidePrice flag */}
            <StyledWrapper
                {...styles.bottomSection}
                className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
            >
                {product?.properties?.hidePrice !== "true" && (
                    <StyledWrapper
                        {...styles.productPriceWrapper}
                        className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
                    >
                        {showPrice && !authorized && (
                            <Typography {...styles.productPrice?.price?.priceText}>
                                {product.unitListPriceDisplay}
                            </Typography>
                        )}
                        {showPrice && authorized && (
                            <>
                                <VisuallyHidden as="label" htmlFor={`${id}-uom`} id={`${id}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice
                                    showLabel={false}
                                    product={productContext}
                                    showSavings={priceShowSavings}
                                    showSavingsAmount={priceShowSavingsAmount}
                                    showSavingsPercent={priceShowSavingsPercent}
                                    extendedStyles={styles.productPrice}
                                />
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        id={`${id}-uom`}
                                        disabled={productUnitOfMeasureDisabling}
                                    />
                                )}
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </>
                        )}
                    </StyledWrapper>
                )}
                {showAttributes && authorized && hasAttributeTypes && (
                    <StyledWrapper
                        {...styles.attributeTypesWrapper}
                        className={`${productTileShortView && "short-view"}`}
                    >
                        <ProductAttributes
                            maximumNumberAttributeTypes={3}
                            extendedStyles={styles.attributes}
                            useInlineStructure={true}
                        />
                    </StyledWrapper>
                )}
                {showAvailability && authorized && (
                    <StyledWrapper {...styles.productAvailabilityWrapper} className="productAvailabilityWrapper">
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    </StyledWrapper>
                )}
                {showAddToCart && authorized && !ableToAddToCart && (
                    <MessagePanel
                        className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
                        messageName="Custom_OutOfStock_Info"
                        extendedStyles={{ wrapper: styles.outOfStockInfoMessageWrapper }}
                    />
                )}
                {showAddToCart && authorized && ableToAddToCart && (
                    <StyledWrapper
                        {...styles.qtyAddToCartWrapper}
                        className={`${view === "List" && "list-view"} ${productTileShortView && "short-view"}`}
                    >
                        <VisuallyHidden as="label" htmlFor={`${id}-qty`} id={`${id}-qty-label`}>
                            {translate("QTY")}
                        </VisuallyHidden>
                        <ProductQuantityOrdered
                            labelOverride=""
                            extendedStyles={styles.productQuantityOrderedStyles}
                            id={`${id}-qty`}
                            {...{ disabled: productQuantityDisabling }}
                        />
                        <ProductAddToCartButton
                            data-test-selector="addToCartBtn"
                            notes={productNotes}
                            onAddToCartClick={() => trackProductRecommendation(false)}
                            extendedStyles={styles.productAddToCartButtonStyles}
                        />
                    </StyledWrapper>
                )}
                {updateQtyActions && updateQtyActions()}
                {!authorized && (
                    <Typography
                        {...styles.notAuthorizedMessage}
                        as="p"
                        className={`${isThereAnyAction && "add-margin"} ${productTileShortView && "short-view"}`}
                    >
                        {siteMessage("Custom_Product_Availability_Purchase")}
                    </Typography>
                )}
                {isThereAnyAction && (
                    <StyledWrapper
                        {...styles.productTileActions}
                        className={`${isThereMoreThanOneAction() && "more-than-one-action"} ${
                            productTileShortView && "short-view"
                        }`}
                    >
                        {addToListAction && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )}
                        {showCompare && <CompareProductsCheckbox />}
                        {otherActions && otherActions()}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductTile)));
